// Template builder (M14, DRAW_PLAN D12 reduced). Templates are code: each one draws its layout with these
// helpers, in artboard points (screen formats: 1 pt = 1 px). Colours name a brand role ('primary', …) and
// become global swatches, so "My brand" recolours every template; text names a brand font role.
import { newId } from '../model/ids';
import { nearestWeight } from '../text/fontCatalog';
import { IDENTITY, makeLayer, NO_SIDES } from '../model/defaults';
import type {
  AdData,
  AdLang,
  Appearance,
  Artboard,
  Bi,
  ColorRef,
  Doc,
  ImageNode,
  Mat2D,
  Paint,
  Node,
  ShapeNode,
  TextNode,
  TextRole,
} from '../model/types';

export type FormatId = 'ig-square' | 'ig-portrait' | 'story' | 'delivery' | 'board-h' | 'board-v' | 'letter';
export type CategoryId =
  'post' | 'story' | 'delivery' | 'board' | 'menu' | 'special' | 'promo' | 'combo' | 'new' | 'happy';

export const FORMATS: Record<FormatId, { w: number; h: number; print: boolean; name: Bi; px: string }> = {
  'ig-square': {
    w: 1080,
    h: 1080,
    print: false,
    name: { en: 'Instagram post 1:1', es: 'Publicación de Instagram 1:1' },
    px: '1080 × 1080',
  },
  'ig-portrait': {
    w: 1080,
    h: 1350,
    print: false,
    name: { en: 'Instagram post 4:5', es: 'Publicación de Instagram 4:5' },
    px: '1080 × 1350',
  },
  story: {
    w: 1080,
    h: 1920,
    print: false,
    name: { en: 'Instagram story 9:16', es: 'Historia de Instagram 9:16' },
    px: '1080 × 1920',
  },
  delivery: {
    w: 1200,
    h: 800,
    print: false,
    name: { en: 'Delivery-app banner', es: 'Banner para app de delivery' },
    px: '1200 × 800',
  },
  'board-h': {
    w: 1920,
    h: 1080,
    print: false,
    name: { en: 'Menu board 16:9', es: 'Pantalla de menú 16:9' },
    px: '1920 × 1080 (4K: 3840 × 2160)',
  },
  'board-v': {
    w: 1080,
    h: 1920,
    print: false,
    name: { en: 'Menu board vertical', es: 'Pantalla de menú vertical' },
    px: '1080 × 1920',
  },
  letter: {
    w: 612,
    h: 792,
    print: true,
    name: { en: 'Letter page (print)', es: 'Página carta (impresión)' },
    px: '8.5 × 11 in',
  },
};

export const CATEGORIES: { id: CategoryId; name: Bi }[] = [
  { id: 'post', name: { en: 'Instagram posts', es: 'Publicaciones de Instagram' } },
  { id: 'story', name: { en: 'Instagram stories', es: 'Historias de Instagram' } },
  { id: 'delivery', name: { en: 'Delivery apps', es: 'Apps de delivery' } },
  { id: 'board', name: { en: 'Menu boards', es: 'Pantallas de menú' } },
  { id: 'menu', name: { en: 'Printed menus', es: 'Menús impresos' } },
  { id: 'special', name: { en: 'Daily special', es: 'Especial del día' } },
  { id: 'promo', name: { en: 'Promos and discounts', es: 'Promociones y descuentos' } },
  { id: 'combo', name: { en: 'Combos', es: 'Combos' } },
  { id: 'new', name: { en: 'New dish', es: 'Plato nuevo' } },
  { id: 'happy', name: { en: 'Happy hour', es: 'Happy hour' } },
];

/** M20 style families (the picker's sections). 'classic' = the M14 set, kept in code but not shown. */
export type FamilyId = 'clean' | 'bold' | 'elegant' | 'fresh' | 'promo' | 'classic';
export const FAMILIES: { id: Exclude<FamilyId, 'classic'>; name: Bi; note: Bi }[] = [
  {
    id: 'clean',
    name: { en: 'Clean', es: 'Limpio' },
    note: { en: 'White, lots of space, thin type', es: 'Blanco, mucho espacio, letra fina' },
  },
  {
    id: 'bold',
    name: { en: 'Bold Street', es: 'Urbano' },
    note: { en: 'Big condensed type, one strong colour', es: 'Letra grande y condensada, un color fuerte' },
  },
  {
    id: 'elegant',
    name: { en: 'Elegant', es: 'Elegante' },
    note: { en: 'Dark, serif headline, restrained', es: 'Oscuro, titular con serifa, sobrio' },
  },
  {
    id: 'fresh',
    name: { en: 'Fresh Market', es: 'Mercado fresco' },
    note: { en: 'Light natural tones, a handwritten touch', es: 'Tonos naturales claros, un toque a mano' },
  },
  {
    id: 'promo',
    name: { en: 'Promo', es: 'Promoción' },
    note: { en: 'Price first: 2x1, combos, happy hour', es: 'El precio primero: 2x1, combos, happy hour' },
  },
];

export type TemplateDef = {
  id: string;
  category: CategoryId;
  format: FormatId;
  name: Bi;
  /** M20 style family; 'classic' templates are not shown in the picker (still open from saved ads). */
  family?: FamilyId;
  /** M20: the photo the layout is made for: a cut-out dish (transparent PNG) or a full photo. */
  slot?: 'cutout' | 'full';
  /** Sample content this template starts with (merged over SAMPLE_AD). */
  ad?: Partial<AdData>;
  build: (b: Builder) => void;
};

export type BrandRole = 'primary' | 'secondary' | 'accent' | 'background' | 'text';
export const BRAND_ROLES: BrandRole[] = ['primary', 'secondary', 'accent', 'background', 'text'];
/**
 * M20 photo tone: one colour sampled from the ad's photo (src/design/ad/tone.ts), as a mid tone, a very light
 * tint (backgrounds) and a deep shade (dark backgrounds). Global swatches 'tone-<role>'.
 */
export type ToneRole = 'tone' | 'toneLight' | 'toneDark';
export const TONE_SWATCH: Record<ToneRole, string> = {
  tone: 'tone-base',
  toneLight: 'tone-light',
  toneDark: 'tone-dark',
};
/** Photo tones before a photo is in the ad: a warm toasted-bread tone (most dishes are warm). */
export const DEFAULT_TONE: Record<ToneRole, [number, number, number]> = {
  tone: [0.71, 0.45, 0.27],
  toneLight: [0.96, 0.93, 0.89],
  toneDark: [0.17, 0.12, 0.09],
};
/**
 * Text colours chosen for contrast from the brand (computed when the brand or the photo tone changes, see
 * instantiate.ts deriveTextColors), so any brand meets WCAG AA without changing its values:
 *   onPrimary / onSecondary            large text on a primary / secondary fill (≥ 3:1)
 *   onPrimarySmall / onSecondarySmall  normal-size text on those fills (≥ 4.5:1)
 *   accentText                         brand-coloured text on white / the light photo tone (≥ 4.5:1)
 *   accentOnSecondary                  brand-coloured LARGE text on the secondary colour (≥ 3:1)
 */
export type TextRoleColor =
  'onPrimary' | 'onPrimarySmall' | 'onSecondary' | 'onSecondarySmall' | 'accentText' | 'accentOnSecondary';
export const TEXT_SWATCH: Record<TextRoleColor, string> = {
  onPrimary: 'brand-on-primary',
  onPrimarySmall: 'brand-on-primary-small',
  onSecondary: 'brand-on-secondary',
  onSecondarySmall: 'brand-on-secondary-small',
  accentText: 'brand-accent-text',
  accentOnSecondary: 'brand-accent-on-secondary',
};
type ColName = BrandRole | ToneRole | TextRoleColor | 'white' | 'black' | `#${string}`;
/** A colour: a brand role, a photo tone, 'white' / 'black', or '#rrggbb'; optional alpha. */
export type Col = ColName | [ColName, number];

export function colorRef(c: Col): ColorRef {
  const [name, alpha] = Array.isArray(c) ? c : [c, 1];
  if (name === 'white') return { rgb: [1, 1, 1], alpha };
  if (name === 'black') return { rgb: [0, 0, 0], alpha };
  if (name in TONE_SWATCH) return { swatchId: TONE_SWATCH[name as ToneRole], alpha };
  if (name in TEXT_SWATCH) return { swatchId: TEXT_SWATCH[name as TextRoleColor], alpha };
  if (name.startsWith('#'))
    return {
      rgb: [1, 3, 5].map((i) => parseInt(name.slice(i, i + 2), 16) / 255) as [number, number, number],
      alpha,
    };
  return { swatchId: `brand-${name}`, alpha };
}

type PaintOpts = {
  fill?: Col | null;
  stroke?: Col | null;
  strokeW?: number;
  dash?: number[];
  opacity?: number;
};

function appearance(o: PaintOpts): Appearance {
  return {
    fills: o.fill ? [{ paint: { kind: 'solid', color: colorRef(o.fill) }, opacity: 1, blend: 'normal' }] : [],
    strokes: o.stroke
      ? [
          {
            paint: { kind: 'solid', color: colorRef(o.stroke) },
            width: o.strokeW ?? 2,
            cap: 'round',
            join: 'round',
            miter: 10,
            dash: o.dash ?? [],
            dashOffset: 0,
            align: 'center',
            opacity: 1,
            blend: 'normal',
          },
        ]
      : [],
    opacity: o.opacity ?? 1,
    blend: 'normal',
    effects: [],
  };
}

const translate = (x: number, y: number): Mat2D => [1, 0, 0, 1, x, y];

export type TextOpts = {
  role: TextRole;
  lang?: AdLang;
  item?: number;
  /** Literal text for role 'free'. */
  text?: string;
  font?: 'heading' | 'body';
  weight?: number;
  size: number;
  color?: Col;
  align?: 'left' | 'center' | 'right';
  valign?: 'top' | 'middle' | 'bottom';
  caps?: boolean;
  tracking?: number;
  leading?: number;
  maxLines?: number;
  /** Smallest size shrink-to-fit may use (default 45% of size). */
  min?: number;
  /** M20: a fixed bundled family (not the brand's heading/body font). */
  family?: string;
  opacity?: number;
  name?: string;
};

export class Builder {
  readonly W: number;
  readonly H: number;
  readonly nodes: Node[] = [];
  background: Col = 'background';
  /** M20: no text (nor its shrink-to-fit minimum) below this size; the M20 templates set 3% of the short side. */
  minText = 0;
  constructor(readonly format: FormatId) {
    this.W = FORMATS[format].w;
    this.H = FORMATS[format].h;
  }
  private push<T extends Node>(n: T): T {
    this.nodes.push(n);
    return n;
  }
  bg(c: Col) {
    this.background = c;
  }
  rect(x: number, y: number, w: number, h: number, o: PaintOpts & { r?: number } = {}): ShapeNode {
    const r = o.r ?? 0;
    return this.push({
      id: newId('n'),
      kind: 'shape',
      visible: true,
      locked: false,
      transform: IDENTITY,
      appearance: appearance(o),
      shape: { type: 'rect', x, y, w, h, radii: [r, r, r, r] },
    });
  }
  ellipse(cx: number, cy: number, rx: number, ry: number, o: PaintOpts = {}): ShapeNode {
    return this.push({
      id: newId('n'),
      kind: 'shape',
      visible: true,
      locked: false,
      transform: IDENTITY,
      appearance: appearance(o),
      shape: { type: 'ellipse', cx, cy, rx, ry },
    });
  }
  star(cx: number, cy: number, r1: number, r2: number, points: number, o: PaintOpts = {}): ShapeNode {
    return this.push({
      id: newId('n'),
      kind: 'shape',
      visible: true,
      locked: false,
      transform: IDENTITY,
      appearance: appearance(o),
      shape: { type: 'star', cx, cy, r1, r2, points },
    });
  }
  line(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: Col,
    width = 2,
    dash: number[] = [],
  ): ShapeNode {
    return this.push({
      id: newId('n'),
      kind: 'shape',
      visible: true,
      locked: false,
      transform: IDENTITY,
      appearance: appearance({ stroke: color, strokeW: width, dash }),
      shape: { type: 'line', x1, y1, x2, y2 },
    });
  }
  text(x: number, y: number, w: number, h: number, o: TextOpts): TextNode {
    return this.push({
      id: newId('t'),
      name: o.name,
      kind: 'text',
      visible: true,
      locked: false,
      transform: translate(x, y),
      appearance: appearance({ opacity: o.opacity }),
      text: o.text ?? '',
      box: {
        w,
        h,
        maxLines: o.maxLines ?? 2,
        minSize: Math.max(this.minText, o.min ?? Math.round(o.size * 0.45)),
      },
      style: {
        ...(o.family
          ? { ...nearestWeight(o.family, o.weight ?? 400), fontRole: undefined }
          : {
              family: 'Montserrat',
              weight: o.weight ?? (o.font === 'body' ? 400 : 800),
              fontRole: o.font ?? 'heading',
            }),
        size: o.size,
        color: colorRef(o.color ?? 'text'),
        tracking: o.tracking ?? 0,
        leading: o.leading ?? 1.12,
        caps: o.caps ?? false,
        align: o.align ?? 'left',
        valign: o.valign ?? 'top',
      },
      bind: o.role === 'free' ? { role: 'free' } : { role: o.role, lang: o.lang, item: o.item },
    });
  }
  /** Bilingual pair: Spanish first (bigger), English under it (smaller, softer). Returns the bottom y. */
  bi(
    x: number,
    y: number,
    w: number,
    role: TextRole,
    o: Omit<TextOpts, 'role' | 'lang'> & {
      enSize?: number;
      gap?: number;
      esH?: number;
      enH?: number;
      item?: number;
      enLines?: number;
    },
  ): number {
    const esH = o.esH ?? Math.round(o.size * 1.15 * (o.maxLines ?? 1));
    const enSize = o.enSize ?? Math.round(o.size * 0.6);
    const enH = o.enH ?? Math.round(enSize * 1.25);
    this.text(x, y, w, esH, { ...o, role, lang: 'es' });
    const y2 = y + esH + (o.gap ?? 4);
    this.text(x, y2, w, enH, {
      ...o,
      role,
      lang: 'en',
      size: enSize,
      maxLines: o.enLines ?? 1,
      font: o.font === 'heading' ? 'body' : (o.font ?? 'body'),
      weight: o.font === 'heading' ? 600 : (o.weight ?? 400),
      opacity: (o.opacity ?? 1) * 0.85,
      caps: false,
    });
    return y2 + enH;
  }
  photo(
    x: number,
    y: number,
    w: number,
    h: number,
    o: {
      shape?: 'rect' | 'rounded' | 'ellipse';
      r?: number;
      stroke?: Col;
      strokeW?: number;
      fill?: Col;
      fit?: 'cover' | 'contain';
      /** M20: image size inside the frame (< 1 leaves room for a shadow inside the frame). */
      zoom?: number;
      /** M20: soft drop shadow following the image's alpha (a floating cut-out). */
      shadow?: { blur: number; y: number; opacity: number; color?: Col };
    } = {},
  ): ImageNode {
    const ap = appearance({ stroke: o.stroke, strokeW: o.strokeW, fill: o.fill });
    if (o.shadow)
      ap.effects = [
        {
          kind: 'dropShadow',
          enabled: true,
          params: {
            blur: o.shadow.blur,
            x: 0,
            y: o.shadow.y,
            color: { ...colorRef(o.shadow.color ?? 'black'), alpha: o.shadow.opacity },
          },
        },
      ];
    return this.push({
      id: newId('img'),
      name: 'Photo',
      kind: 'image',
      visible: true,
      locked: false,
      transform: translate(x, y),
      appearance: ap,
      assetId: null,
      iw: 0,
      ih: 0,
      frame: { w, h, shape: o.shape ?? 'rect', radius: o.r ?? 0 },
      fit: o.fit ?? 'cover',
      zoom: o.zoom ?? 1,
      offsetX: 0,
      offsetY: 0,
      role: 'photo',
    });
  }
  logo(
    x: number,
    y: number,
    w: number,
    h: number,
    o: {
      fill?: Col;
      r?: number;
      /** on a light background (uses the brand's light-background logo) */ onLight?: boolean;
    } = {},
  ): ImageNode {
    return this.push({
      id: newId('img'),
      name: 'Logo',
      kind: 'image',
      visible: true,
      locked: false,
      transform: translate(x, y),
      appearance: appearance({ fill: o.fill }),
      assetId: null,
      iw: 0,
      ih: 0,
      frame: { w, h, shape: o.r ? 'rounded' : 'rect', radius: o.r ?? 0 },
      fit: 'contain',
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      role: 'logo',
      ...(o.onLight ? { onLight: true } : {}),
    });
  }
  /**
   * M20: a soft gradient band (e.g. dark at the bottom of a full-bleed photo, fading to clear). `stops` are
   * [offset 0..1 along `dir`, colour]; 'down' runs top → bottom.
   */
  grad(
    x: number,
    y: number,
    w: number,
    h: number,
    stops: [number, Col][],
    dir: 'down' | 'up' | 'right' | 'left' = 'down',
  ): ShapeNode {
    const geom = {
      down: { x1: x, y1: y, x2: x, y2: y + h },
      up: { x1: x, y1: y + h, x2: x, y2: y },
      right: { x1: x, y1: y, x2: x + w, y2: y },
      left: { x1: x + w, y1: y, x2: x, y2: y },
    }[dir];
    const paint: Paint = {
      kind: 'linear',
      geom,
      stops: stops.map(([offset, c]) => ({ offset, color: colorRef(c) })),
    };
    const ap = appearance({});
    ap.fills = [{ paint, opacity: 1, blend: 'normal' }];
    return this.push({
      id: newId('n'),
      kind: 'shape',
      visible: true,
      locked: false,
      transform: IDENTITY,
      appearance: ap,
      shape: { type: 'rect', x, y, w, h, radii: [0, 0, 0, 0] },
    });
  }

  /** A price tag: a filled shape with the price centred. */
  priceBadge(
    cx: number,
    cy: number,
    r: number,
    o: { fill?: Col; color?: Col; star?: boolean; role?: 'price' | 'oldPrice' } = {},
  ) {
    if (o.star) this.star(cx, cy, r, r * 0.84, 16, { fill: o.fill ?? 'secondary' });
    else this.ellipse(cx, cy, r, r, { fill: o.fill ?? 'secondary' });
    this.text(cx - r * 0.82, cy - r * 0.42, r * 1.64, r * 0.84, {
      role: o.role ?? 'price',
      size: r * 0.5,
      color: o.color ?? 'text',
      align: 'center',
      valign: 'middle',
      maxLines: 1,
    });
  }

  /** The document: one artboard, one layer, the 5 brand swatches and the 3 photo tones. */
  doc(name: string): Doc {
    const f = FORMATS[this.format];
    const ab: Artboard = {
      id: newId('ab'),
      name,
      x: 0,
      y: 0,
      w: f.w,
      h: f.h,
      bleed: f.print ? { top: 9, right: 9, bottom: 9, left: 9 } : { ...NO_SIDES },
      margins: f.print ? { top: 27, right: 27, bottom: 27, left: 27 } : { ...NO_SIDES },
      background: { kind: 'solid', color: colorRef(this.background) },
    };
    const layer = makeLayer('Ad');
    layer.children = this.nodes;
    return {
      v: 1,
      id: newId('doc'),
      name,
      colorMode: 'rgb',
      rasterDpi: f.print ? 300 : 72,
      artboards: [ab],
      layers: [layer],
      swatches: [
        ...BRAND_ROLES.map((r) => ({
          id: `brand-${r}`,
          name: r,
          color: { rgb: [0, 0, 0] as [number, number, number], alpha: 1 },
          global: true,
        })),
        ...(Object.keys(TONE_SWATCH) as ToneRole[]).map((r) => ({
          id: TONE_SWATCH[r],
          name: r,
          color: { rgb: [...DEFAULT_TONE[r]] as [number, number, number], alpha: 1 },
          global: true,
        })),
        ...(Object.keys(TEXT_SWATCH) as TextRoleColor[]).map((r) => ({
          id: TEXT_SWATCH[r],
          name: r,
          color: { rgb: [1, 1, 1] as [number, number, number], alpha: 1 },
          global: true,
        })),
      ],
      swatchGroups: [],
      charStyles: [],
      paraStyles: [],
      graphicStyles: [],
      symbols: [],
      fonts: [],
      guides: [],
      units: f.print ? 'in' : 'px',
    };
  }
}
